import { Fragment, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Details, Flex, Page, SegmentedControl, Text } from '@servicetitan/anvil2';

import { ChatHeaderNew } from '../../common/components/chat-layout/chat-header-new';

import '../styles/info-page.css';

type InfoViewType = 'faq' | 'updates';

export const InfoPage = () => {
    const [view, setView] = useState<InfoViewType>('faq');
    const history = useHistory();

    const showUpdatesTab = false;
    return (
        <Fragment>
            <div className="info-page-header">
                <ChatHeaderNew
                    onNavigationButtonClick={() => history.goBack()}
                    icon="close"
                    hideInfoButton
                />
            </div>
            <Page className="info-page" style={{ marginTop: 56 }}>
                <Page.Content>
                    <Flex direction="column" gap="4">
                        <Text size="small">
                            Atlas is an AI assistant designed to help you complete your work.
                        </Text>
                        {showUpdatesTab && (
                            <SegmentedControl
                                fill
                                defaultSelected="faq"
                                aria-label="Transportations"
                                onChange={value => setView(value as InfoViewType)}
                            >
                                <SegmentedControl.Segment value="faq">FAQ</SegmentedControl.Segment>
                                <SegmentedControl.Segment value="updates">
                                    Updates
                                </SegmentedControl.Segment>
                            </SegmentedControl>
                        )}

                        {view === 'faq' && (
                            <Flex direction="column" gap="4">
                                <Details defaultExpanded>
                                    <Details.Summary>What I can do today:</Details.Summary>
                                    <Details.Content>
                                        <ul>
                                            <li>
                                                Answer questions via Chat about data in
                                                ServiceTitan, for these datasets:
                                            </li>

                                            <ul>
                                                <li>Customer Information</li>
                                                <li>Location Information</li>
                                                <li>Job History (Summary stats & last 5 jobs)</li>
                                                <li>Future Jobs (Next 5 jobs)</li>
                                                <li>
                                                    Invoice History (Summary stats & last 5
                                                    invoices)
                                                </li>
                                                <li>
                                                    Estimate History (Summary stats & last 5
                                                    estimates)
                                                </li>
                                                <li>Installed Equipment</li>
                                                <li>Memberships (only basic info)</li>
                                                <li>Service Agreements (only basic info)</li>
                                            </ul>
                                        </ul>
                                    </Details.Content>
                                </Details>
                                <Details>
                                    <Details.Summary>What I can do soon:</Details.Summary>
                                    <Details.Content>
                                        <ul>
                                            <li>
                                                Answer questions via Chat about data in ServiceTitan
                                                for the following datasets:
                                            </li>

                                            <ul>
                                                <li>Customer Information</li>
                                                <li>Location Information</li>
                                                <li>Job History (Summary stats & last 100 jobs)</li>
                                                <li>Future Jobs (Next 5 jobs)</li>
                                                <li>
                                                    Invoice History (Summary stats & last 100
                                                    invoices)
                                                </li>
                                                <li>
                                                    Estimate History (Summary stats & last 100
                                                    estimates)
                                                </li>
                                                <li>Installed Equipment</li>
                                                <li>
                                                    <b>
                                                        Memberships (equipment, recurring services,
                                                        service events)
                                                    </b>
                                                </li>
                                                <li>
                                                    <b>
                                                        Service Agreements (equipment, visit
                                                        schedule, services, materials)
                                                    </b>
                                                </li>
                                                <li>
                                                    <b>Forms</b>
                                                </li>
                                                <li>
                                                    <b>Inventory</b>
                                                </li>
                                                <li>
                                                    <b>Pricebook</b>
                                                </li>
                                            </ul>
                                            <li>
                                                Answer questions via <b>Voice</b> about data in
                                                ServiceTitan
                                            </li>
                                            <li>
                                                Summarize critical intelligence for you when you
                                                arrive at the job
                                            </li>
                                            <li>
                                                Complete <b>actions</b> on your behalf in
                                                ServiceTitan
                                            </li>
                                            <ul>
                                                <li>
                                                    E.g. Dispatch/Arrive, Add a Note, Book a Job,
                                                    etc.
                                                </li>
                                            </ul>
                                            <li>…and lots lots more!</li>
                                        </ul>
                                    </Details.Content>
                                </Details>

                                <Details>
                                    <Details.Summary>Best Practices</Details.Summary>
                                    <Details.Content>
                                        <ul>
                                            <li>
                                                <b>Be Specific!</b> 🎯 Atlas does better when you
                                                are specific, and use ServiceTitan terminology. For
                                                example:
                                            </li>
                                            <ul>
                                                <li>
                                                    If you want to know about previous estimates,
                                                    specify if you want to look at <b>sold</b>{' '}
                                                    estimates or <b>open</b> estimates.
                                                </li>
                                                <li>
                                                    If the customer has multiple locations, specify
                                                    if you are looking for information about that
                                                    customer, or that location (we have both) - e.g.
                                                    “show me the last job for this <b>customer</b>”
                                                    vs “show me the last job for this{' '}
                                                    <b>location</b>”
                                                </li>
                                            </ul>
                                            <li>
                                                The AI is still learning industry lingo 📚, please
                                                be patient 🙏
                                            </li>
                                            <ul>
                                                <li>
                                                    We know sometimes “jobs” are referred to as
                                                    “calls”, however, we also have actual{' '}
                                                    <b>calls</b> data in Atlas (like…
                                                    <b>phone calls</b> 📞). So if you are asking
                                                    about the current, previous, or future jobs, try
                                                    to use the word “job”.
                                                </li>
                                                <li>
                                                    We are trying to teach the AI different terms -
                                                    for example we taught it that “estimates” can be
                                                    called “quotes”, and that an “equipment” can be
                                                    called a “unit”, but we are not the experts (we
                                                    wish we were more down with the slang 🤙), so
                                                    please go easy on us, and try and use the
                                                    ServiceTitan specific wording to get the best
                                                    results possible.
                                                </li>
                                                <ul>
                                                    <li>
                                                        E.g use “estimate” or “membership” or
                                                        “material” or “invoice” etc. to refer to the
                                                        data as it exists in ServiceTitan
                                                    </li>
                                                </ul>
                                            </ul>
                                            <li>We are actively expanding our coverage</li>
                                            <ul>
                                                <li>
                                                    While we have a LOT of data in FieldAssist
                                                    today, we still have some gaps.
                                                </li>
                                                <li>
                                                    For example Forms, Inventory, and Pricebook are
                                                    not covered today.
                                                </li>
                                                <li>Plus some areas have partial coverage</li>
                                                <ul>
                                                    <li>
                                                        We have the last 5 invoices in full detail,
                                                        but we don't have ALL invoices.
                                                    </li>
                                                    <li>
                                                        We have basic info about Service agreements
                                                        (name, status, start/end date), but not full
                                                        details (equipment, visits, materials, etc.)
                                                    </li>
                                                </ul>
                                                <li>
                                                    Please check back here in the “updates” tab to
                                                    check out what's new and see our current
                                                    coverage status.
                                                </li>
                                            </ul>
                                        </ul>
                                    </Details.Content>
                                </Details>
                            </Flex>
                        )}

                        {view === 'updates' && <div />}
                    </Flex>
                </Page.Content>
            </Page>
        </Fragment>
    );
};
